import React, { useState, Component } from 'react';
import { CalendarIcon, SearchIcon, CheckCircleIcon, ClockIcon, ListTodoIcon, ArrowUpDownIcon, FilterIcon, PackageIcon, UtensilsIcon, Loader2Icon, ScissorsIcon, BoxIcon, XIcon, ScanIcon, PackageSearchIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react';
import { PinVerificationModal } from './PinVerificationModal';
type ProductionStatus = 'planned' | 'in_progress' | 'completed';
type ProductionStep = 'kutterei' | 'fuellerei' | 'raeucherei' | 'schaelerei' | 'verpackung';
const statusLabels: Record<ProductionStatus, {
  label: string;
  color: string;
}> = {
  planned: {
    label: 'Geplant',
    color: 'bg-gray-100 text-gray-700'
  },
  in_progress: {
    label: 'In Bearbeitung',
    color: 'bg-blue-100 text-blue-700'
  },
  completed: {
    label: 'Abgeschlossen',
    color: 'bg-green-100 text-green-700'
  }
};
const productionSteps: Record<ProductionStep, {
  label: string;
  icon: ComponentType<any>;
}> = {
  kutterei: {
    label: 'Kutterei',
    icon: BoxIcon
  },
  fuellerei: {
    label: 'Füllerei',
    icon: UtensilsIcon
  },
  raeucherei: {
    label: 'Räucherei',
    icon: Loader2Icon
  },
  schaelerei: {
    label: 'Schälerei',
    icon: ScissorsIcon
  },
  verpackung: {
    label: 'Verpackung',
    icon: PackageIcon
  }
};
function getCurrentStep(status: ProductionStatus): ProductionStep | null {
  if (status === 'planned') return null;
  if (status === 'completed') return 'verpackung';
  const steps = Object.keys(productionSteps) as ProductionStep[];
  const randomIndex = Math.floor(Math.random() * (steps.length - 1));
  return steps[randomIndex];
}
function ProductionTimeline({
  currentStep
}: {
  currentStep: ProductionStep | null;
}) {
  return <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-1047">
      <div className="relative flex justify-between" data-id="element-1048">
        {Object.entries(productionSteps).map(([step, {
        label,
        icon: Icon
      }], index) => {
        const isActive = step === currentStep;
        const isPast = currentStep && Object.keys(productionSteps).indexOf(step) < Object.keys(productionSteps).indexOf(currentStep);
        return <div key={step} className="flex flex-col items-center relative z-10" data-id="element-1049">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? 'bg-blue-500 text-white' : isPast ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-1050">
                  <Icon className="h-4 w-4" data-id="element-1051" />
                </div>
                <span className="text-xs mt-1 font-medium text-gray-600" data-id="element-1052">
                  {label}
                </span>
              </div>;
      })}
        <div className="absolute top-4 left-0 w-full h-[2px] bg-gray-200 -z-10" data-id="element-1053">
          {currentStep && <div className="absolute top-0 left-0 h-full bg-green-500 transition-all duration-500" style={{
          width: `${(Object.keys(productionSteps).indexOf(currentStep) + 1) * 25}%`
        }} data-id="element-1054" />}
        </div>
      </div>
    </div>;
}
const demoApprovedPlans = [{
  date: new Date().toISOString().split('T')[0],
  items: [{
    product: {
      name: 'Pfefferbeißer Pikante LIDL QS ITW',
      productNumber: '130901230425'
    },
    quantity: 600,
    status: 'in_progress',
    startTime: new Date().toISOString(),
    currentStep: getCurrentStep('in_progress')
  }, {
    product: {
      name: 'Pfefferbeißer Pikante LIDL QS ITW',
      productNumber: '230901230425'
    },
    quantity: 600,
    status: 'in_progress',
    startTime: new Date().toISOString(),
    currentStep: getCurrentStep('in_progress')
  }],
  status: 'approved',
  approvedBy: 'Max Mustermann',
  approvedAt: new Date().toISOString()
}];
function convertPlanToTrackingItems(plan: any) {
  return plan.items.map((item: any) => ({
    id: `${plan.date}-${item.product.productNumber}`,
    planDate: plan.date,
    productName: item.product.name,
    productNumber: item.product.productNumber,
    quantity: item.quantity,
    status: item.status as ProductionStatus,
    currentStep: item.currentStep,
    startTime: item.startTime,
    completionTime: item.completionTime
  }));
}
interface BatchDetails {
  batchNumber: string;
  timestamp: string;
  materials?: {
    name: string;
    batchNumber: string;
    quantity: string;
    unit: string;
    details?: {
      batchNumber: string;
      length: string;
      caliber: string;
      quantity: string;
    }[];
  }[];
  products?: {
    name: string;
    batchNumber: string;
    quantity: string;
    unit: string;
  }[];
  palletNumber?: string;
  customerBatchNumber?: string;
}
interface ProductionStepDetails {
  step: ProductionStep;
  status: 'completed' | 'in_progress' | 'pending';
  startTime?: string;
  completionTime?: string;
  batchDetails: BatchDetails;
  operator?: string;
}
interface TrackingDetails {
  mainBatchNumber: string;
  productionSteps: ProductionStepDetails[];
  currentStep: ProductionStep | null;
  palletInfo?: {
    palletNumber: string;
    customerBatchNumber: string;
    totalQuantity: number;
  };
}
const mockTrackingDetails: TrackingDetails = {
  mainBatchNumber: '130901230425',
  productionSteps: [{
    step: 'kutterei',
    status: 'completed',
    startTime: '2024-01-20T08:00:00',
    completionTime: '2024-01-20T09:30:00',
    operator: 'K. Schmidt',
    batchDetails: {
      batchNumber: 'KUT-2024-0001',
      timestamp: '2024-01-20T08:00:00',
      materials: [{
        name: 'Schweineschulter',
        batchNumber: '116425',
        quantity: '579,5',
        unit: 'kg'
      }, {
        name: 'Maturation Starter',
        batchNumber: '4414763178',
        quantity: '250',
        unit: 'kg'
      }, {
        name: 'Gewürzmischung Pfefferbeisserpikant',
        batchNumber: '20250415 - 38708',
        quantity: '4,88',
        unit: 'kg'
      }, {
        name: 'NPS',
        batchNumber: '20250325-37063',
        quantity: '4,837',
        unit: 'kg'
      }],
      products: [{
        name: 'Schweine-Bratwurst',
        batchNumber: 'BR-2024-0001',
        quantity: '250',
        unit: 'kg'
      }, {
        name: 'Thüringer Rostbratwurst',
        batchNumber: 'BR-2024-0002',
        quantity: '180',
        unit: 'kg'
      }]
    }
  }, {
    step: 'fuellerei',
    status: 'completed',
    startTime: '2024-01-20T09:45:00',
    completionTime: '2024-01-20T11:00:00',
    operator: 'M. Weber',
    batchDetails: {
      batchNumber: 'FUE-2024-0001',
      timestamp: '2024-01-20T09:45:00',
      materials: [{
        name: 'Naturdarm 28/30',
        batchNumber: 'DA-2024-0089',
        quantity: '10',
        unit: 'Bund',
        details: [{
          batchNumber: 'DA-2024-0089-1',
          length: '91',
          caliber: '28/30',
          quantity: '1'
        }, {
          batchNumber: 'DA-2024-0089-2',
          length: '89',
          caliber: '28/30',
          quantity: '1'
        }, {
          batchNumber: 'DA-2024-0089-3',
          length: '92',
          caliber: '28/30',
          quantity: '1'
        }, {
          batchNumber: 'DA-2024-0089-4',
          length: '90',
          caliber: '28/30',
          quantity: '1'
        }, {
          batchNumber: 'DA-2024-0089-5',
          length: '88',
          caliber: '28/30',
          quantity: '1'
        }, {
          batchNumber: 'DA-2024-0089-6',
          length: '93',
          caliber: '28/30',
          quantity: '1'
        }, {
          batchNumber: 'DA-2024-0089-7',
          length: '87',
          caliber: '28/30',
          quantity: '1'
        }, {
          batchNumber: 'DA-2024-0089-8',
          length: '91',
          caliber: '28/30',
          quantity: '1'
        }, {
          batchNumber: 'DA-2024-0089-9',
          length: '89',
          caliber: '28/30',
          quantity: '1'
        }, {
          batchNumber: 'DA-2024-0089-10',
          length: '90',
          caliber: '28/30',
          quantity: '1'
        }]
      }],
      products: [{
        name: 'Schweine-Bratwurst',
        batchNumber: 'BR-2024-0001',
        quantity: '250',
        unit: 'kg'
      }, {
        name: 'Thüringer Rostbratwurst',
        batchNumber: 'BR-2024-0002',
        quantity: '180',
        unit: 'kg'
      }]
    }
  }, {
    step: 'raeucherei',
    status: 'completed',
    startTime: '2024-01-20T11:30:00',
    completionTime: '2024-01-20T14:30:00',
    operator: 'R. Mueller',
    batchDetails: {
      batchNumber: 'RAE-2024-0001',
      timestamp: '2024-01-20T11:30:00',
      materials: [{
        name: 'Räucherliquid',
        batchNumber: 'RL-2024-0012',
        quantity: '5',
        unit: 'L'
      }]
    }
  }, {
    step: 'schaelerei',
    status: 'in_progress',
    startTime: '2024-01-20T14:45:00',
    operator: 'S. Fischer',
    batchDetails: {
      batchNumber: 'SCH-2024-0001',
      timestamp: '2024-01-20T14:45:00',
      palletNumber: 'PAL-2024-0001'
    }
  }, {
    step: 'verpackung',
    status: 'pending',
    batchDetails: {
      batchNumber: 'VER-2024-0001',
      timestamp: '',
      customerBatchNumber: 'KUNDE-2024-001'
    }
  }],
  currentStep: 'schaelerei',
  palletInfo: {
    palletNumber: 'PAL-2024-0001',
    customerBatchNumber: 'KUNDE-2024-001',
    totalQuantity: 1000
  }
};
function MaterialDetailsModal({
  isOpen,
  onClose,
  material
}: {
  isOpen: boolean;
  onClose: () => void;
  material: BatchDetails['materials'][0];
}) {
  if (!isOpen || !material) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1055">
      <div className="bg-white rounded-lg w-[600px] max-h-[80vh] overflow-hidden flex flex-col" data-id="element-1056">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1057">
          <div data-id="element-1058">
            <h2 className="text-xl font-bold" data-id="element-1059">{material.name} - Details</h2>
            <p className="text-sm text-gray-500" data-id="element-1060">
              Chargen-Nr. {material.batchNumber}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-1061">
            <XIcon className="h-5 w-5" data-id="element-1062" />
          </button>
        </div>
        <div className="p-4 overflow-auto" data-id="element-1063">
          <div className="bg-gray-50 rounded-lg overflow-hidden" data-id="element-1064">
            <table className="w-full text-sm" data-id="element-1065">
              <thead className="bg-gray-100" data-id="element-1066">
                <tr data-id="element-1067">
                  <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1068">
                    Chargen-Nr.
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1069">
                    Länge (m)
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1070">
                    Kaliber
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1071">
                    Menge (Bund)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-1072">
                {material.details?.map((detail, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-1073">
                    <td className="px-4 py-2 font-mono text-sm" data-id="element-1074">
                      {detail.batchNumber}
                    </td>
                    <td className="px-4 py-2" data-id="element-1075">{detail.length}</td>
                    <td className="px-4 py-2" data-id="element-1076">{detail.caliber}</td>
                    <td className="px-4 py-2" data-id="element-1077">{detail.quantity}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}
function BatchDetailModal({
  isOpen,
  onClose,
  details
}: {
  isOpen: boolean;
  onClose: () => void;
  details: TrackingDetails;
}) {
  const [selectedMaterial, setSelectedMaterial] = useState<BatchDetails['materials'][0] | null>(null);
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1078">
      <div className="bg-white rounded-lg w-[900px] max-h-[80vh] overflow-hidden flex flex-col" data-id="element-1079">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1080">
          <div data-id="element-1081">
            <h2 className="text-xl font-bold flex items-center gap-2" data-id="element-1082">
              <div className="h-5 w-5 text-[#ec8301]" data-id="element-1083" />
              Chargen-Nr. {details.mainBatchNumber}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-1084">
            <XIcon className="h-5 w-5" data-id="element-1085" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6" data-id="element-1086">
          <div className="space-y-8" data-id="element-1087">
            {details.productionSteps.map((step, index) => <div key={step.step} className={`relative ${index !== details.productionSteps.length - 1 ? 'pb-8 border-l-2 border-gray-200 ml-4' : ''}`} data-id="element-1088">
                <div className="flex items-start" data-id="element-1089">
                  <div className={`absolute -left-4 w-8 h-8 rounded-full flex items-center justify-center
                    ${step.status === 'completed' ? 'bg-green-500' : step.status === 'in_progress' ? 'bg-blue-500' : 'bg-gray-200'}`} data-id="element-1090">
                    {(() => {
                  const Icon = productionSteps[step.step].icon;
                  return <Icon className="h-4 w-4 text-white" data-id="element-1091" />;
                })()}
                  </div>
                  <div className="ml-8 bg-white rounded-lg border border-gray-200 p-4 w-full" data-id="element-1092">
                    <div className="flex justify-between items-start mb-4" data-id="element-1093">
                      <div data-id="element-1094">
                        <h3 className="text-lg font-medium" data-id="element-1095">
                          {productionSteps[step.step].label}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-1096">
                          Chargen-Nr. {step.batchDetails.batchNumber}
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm
                        ${step.status === 'completed' ? 'bg-green-100 text-green-800' : step.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1097">
                        {step.status === 'completed' ? 'Abgeschlossen' : step.status === 'in_progress' ? 'In Bearbeitung' : 'Ausstehend'}
                      </div>
                    </div>
                    {step.batchDetails.materials && step.batchDetails.materials.length > 0 && <div className="mt-4" data-id="element-1098">
                          <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1099">
                            Verwendete Rohstoffe:
                          </h4>
                          <div className="bg-gray-50 rounded-lg overflow-hidden" data-id="element-1100">
                            <table className="w-full text-sm" data-id="element-1101">
                              <thead className="bg-gray-100" data-id="element-1102">
                                <tr data-id="element-1103">
                                  <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1104">
                                    Material
                                  </th>
                                  <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1105">
                                    Chargen-Nr.
                                  </th>
                                  <th className="px-4 py-2 text-left font-medium text-gray-600" data-id="element-1106">
                                    Menge
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200" data-id="element-1107">
                                {step.batchDetails.materials.map((material, idx) => <tr key={idx} className={`hover:bg-gray-50 ${material.details ? 'cursor-pointer' : ''}`} onClick={() => material.details && setSelectedMaterial(material)} data-id="element-1108">
                                      <td className="px-4 py-2" data-id="element-1109">
                                        <div className="flex items-center gap-2" data-id="element-1110">
                                          {material.name}
                                          {material.details && <span className="text-xs text-blue-500" data-id="element-1111">
                                              (Details anzeigen)
                                            </span>}
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 font-mono text-sm" data-id="element-1112">
                                        {material.batchNumber}
                                      </td>
                                      <td className="px-4 py-2" data-id="element-1113">
                                        {material.quantity} {material.unit}
                                      </td>
                                    </tr>)}
                              </tbody>
                            </table>
                          </div>
                        </div>}
                    {step.operator && <div className="mt-4 text-sm text-gray-500" data-id="element-1114">
                        Bearbeiter: {step.operator}
                      </div>}
                    {step.startTime && <div className="mt-2 text-sm text-gray-500" data-id="element-1115">
                        Datum:{' '}
                        {new Date(step.startTime).toLocaleDateString('de-DE')}
                      </div>}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      <MaterialDetailsModal isOpen={!!selectedMaterial} onClose={() => setSelectedMaterial(null)} material={selectedMaterial!} data-id="element-1116" />
    </div>;
}
function QuantityEditModal({
  isOpen,
  onClose,
  item,
  onUpdate
}: {
  isOpen: boolean;
  onClose: () => void;
  item: any;
  onUpdate: (quantities: number[]) => void;
}) {
  const [quantities, setQuantities] = useState([item.quantity]);
  if (!isOpen) return null;
  const addQuantity = () => {
    setQuantities([...quantities, 0]);
  };
  const updateQuantity = (index: number, value: number) => {
    const newQuantities = [...quantities];
    newQuantities[index] = value;
    setQuantities(newQuantities);
  };
  const removeQuantity = (index: number) => {
    setQuantities(quantities.filter((_, i) => i !== index));
  };
  const handleSubmit = () => {
    onUpdate(quantities);
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1117">
      <div className="bg-white rounded-lg p-6 w-[400px]" data-id="element-1118">
        <div className="flex justify-between items-center mb-4" data-id="element-1119">
          <h2 className="text-lg font-medium" data-id="element-1120">Mengen bearbeiten</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-1121">
            <XIcon className="h-5 w-5" data-id="element-1122" />
          </button>
        </div>
        <div className="space-y-4" data-id="element-1123">
          {quantities.map((quantity, index) => <div key={index} className="flex items-center gap-2" data-id="element-1124">
              <input type="number" min="1" value={quantity} onChange={e => updateQuantity(index, parseInt(e.target.value) || 0)} className="flex-1 border border-gray-300 rounded-lg p-2" data-id="element-1125" />
              {quantities.length > 1 && <button onClick={() => removeQuantity(index)} className="text-red-500 hover:text-red-700" data-id="element-1126">
                  <XIcon className="h-5 w-5" data-id="element-1127" />
                </button>}
            </div>)}
          <button onClick={addQuantity} className="w-full border border-gray-300 rounded-lg p-2 hover:bg-gray-50 flex items-center justify-center gap-2" data-id="element-1128">
            <PlusIcon className="h-4 w-4" data-id="element-1129" />
            <span data-id="element-1130">Weitere Menge hinzufügen</span>
          </button>
        </div>
        <div className="mt-6 flex justify-end gap-3" data-id="element-1131">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1132">
            Abbrechen
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-[#ec8301] text-white rounded-lg hover:bg-[#ec8301]/90" data-id="element-1133">
            Speichern
          </button>
        </div>
      </div>
    </div>;
}
export function TrackingSystem() {
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [showPinModal, setShowPinModal] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedStatus, setSelectedStatus] = useState<ProductionStatus | 'all'>('all');
  const [selectedStep, setSelectedStep] = useState<ProductionStep | 'all'>('all');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };
  const allTrackingItems = demoApprovedPlans.flatMap(convertPlanToTrackingItems);
  const filteredItems = allTrackingItems.filter(item => {
    const matchesSearch = item.productName.toLowerCase().includes(searchTerm.toLowerCase()) || item.productNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    const matchesStep = selectedStep === 'all' || item.currentStep === selectedStep;
    const matchesDate = item.planDate === selectedDate.toISOString().split('T')[0];
    return matchesSearch && matchesStatus && matchesStep && matchesDate;
  }).sort((a, b) => {
    const dateA = new Date(a.planDate).getTime();
    const dateB = new Date(b.planDate).getTime();
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });
  const statusCounts = {
    planned: allTrackingItems.filter(item => item.status === 'planned').length,
    in_progress: allTrackingItems.filter(item => item.status === 'in_progress').length,
    completed: allTrackingItems.filter(item => item.status === 'completed').length
  };
  const handleQuantityUpdate = (itemId: string, newQuantities: number[]) => {
    const updatedItems = allTrackingItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          quantity: newQuantities.reduce((a, b) => a + b, 0),
          splitQuantities: newQuantities
        };
      }
      return item;
    });
    // Update your state management here
  };
  if (!isPinVerified) {
    return <div className="p-6 h-full" data-id="element-1134">
        <header className="mb-8" data-id="element-1135">
          <h1 className="text-3xl font-bold text-[#e20019]" data-id="element-1136">
            Laufzettel-Tracking
          </h1>
          <p className="text-gray-600" data-id="element-1137">
            Verfolgen Sie den Produktionsprozess in Echtzeit
          </p>
        </header>
        <div className="flex items-center justify-center h-[calc(100%-8rem)]" data-id="element-1138">
          <div className="text-center" data-id="element-1139">
            <p className="text-xl text-gray-600 mb-2" data-id="element-1140">
              Dieser Bereich ist PIN-geschützt
            </p>
            <p className="text-gray-500 mb-4" data-id="element-1141">
              Bitte geben Sie Ihre PIN ein, um fortzufahren
            </p>
            <button onClick={() => setShowPinModal(true)} className="px-4 py-2 bg-[#ec8301] text-white rounded-lg hover:bg-[#ec8301]/90" data-id="element-1142">
              PIN eingeben
            </button>
          </div>
        </div>
        <PinVerificationModal isOpen={showPinModal} onClose={() => setShowPinModal(false)} onVerify={() => {
        setIsPinVerified(true);
        setShowPinModal(false);
      }} data-id="element-1143" />
      </div>;
  }
  return <div className="p-6 h-full" data-id="element-1144">
      <header className="mb-8" data-id="element-1145">
        <h1 className="text-3xl font-bold text-[#e20019]" data-id="element-1146">
          Laufzettel-Tracking
        </h1>
        <p className="text-gray-600" data-id="element-1147">
          Verfolgen Sie den Produktionsprozess in Echtzeit
        </p>
      </header>
      <div className="bg-white rounded-lg shadow mb-6" data-id="element-1148">
        <div className="p-4 flex items-center justify-between" data-id="element-1149">
          <button onClick={() => navigateDate('prev')} className="p-2 hover:bg-gray-100 rounded-lg" data-id="element-1150">
            <ChevronLeftIcon className="h-6 w-6" data-id="element-1151" />
          </button>
          <div className="text-center" data-id="element-1152">
            <div className="text-2xl font-bold" data-id="element-1153">
              {selectedDate.toLocaleDateString('de-DE', {
              weekday: 'long',
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })}
            </div>
            <div className="text-sm text-gray-500" data-id="element-1154">
              {filteredItems.length} Produktionen
            </div>
          </div>
          <button onClick={() => navigateDate('next')} className="p-2 hover:bg-gray-100 rounded-lg" data-id="element-1155">
            <ChevronRightIcon className="h-6 w-6" data-id="element-1156" />
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow mb-6" data-id="element-1157">
        <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1158">
          <div className="relative" data-id="element-1159">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-1160" />
            <input type="text" placeholder="Produkt suchen..." className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1161" />
          </div>
          <div className="relative" data-id="element-1162">
            <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value as ProductionStatus | 'all')} className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301] appearance-none" data-id="element-1163">
              <option value="all" data-id="element-1164">
                Alle Status ({allTrackingItems.length})
              </option>
              <option value="planned" data-id="element-1165">Geplant ({statusCounts.planned})</option>
              <option value="in_progress" data-id="element-1166">
                In Bearbeitung ({statusCounts.in_progress})
              </option>
              <option value="completed" data-id="element-1167">
                Abgeschlossen ({statusCounts.completed})
              </option>
            </select>
            <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-1168" />
          </div>
          <div className="relative" data-id="element-1169">
            <select value={selectedStep} onChange={e => setSelectedStep(e.target.value as ProductionStep | 'all')} className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301] appearance-none" data-id="element-1170">
              <option value="all" data-id="element-1171">Alle Abteilungen</option>
              {Object.entries(productionSteps).map(([step, {
              label
            }]) => <option key={step} value={step} data-id="element-1172">
                  {label}
                </option>)}
            </select>
            <BoxIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-1173" />
          </div>
          <button onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1174">
            <ArrowUpDownIcon className="h-4 w-4" data-id="element-1175" />
            <span data-id="element-1176">Sortierung {sortOrder === 'asc' ? '↑' : '↓'}</span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1177">
        {filteredItems.map(item => <div key={item.id} onClick={() => setSelectedItem(item.id)} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer" data-id="element-1178">
            <div className="p-4" data-id="element-1179">
              <div className="flex items-start justify-between mb-3" data-id="element-1180">
                <div data-id="element-1181">
                  <h3 className="font-medium" data-id="element-1182">{item.productName}</h3>
                  <p className="text-sm text-gray-500" data-id="element-1183">{item.productNumber}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${statusLabels[item.status].color}`} data-id="element-1184">
                  {statusLabels[item.status].label}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-3" data-id="element-1185">
                <div className="flex items-center gap-1" data-id="element-1186">
                  <PackageIcon className="h-4 w-4" data-id="element-1187" />
                  <span className={item.status === 'planned' ? 'cursor-pointer hover:text-[#ec8301]' : ''} onClick={e => {
                if (item.status === 'planned') {
                  e.stopPropagation();
                  setEditingItem(item.id);
                }
              }} data-id="element-1188">
                    {item.quantity} Stück
                    {item.status === 'planned' && <span className="ml-1 text-[#ec8301]" data-id="element-1189">(bearbeiten)</span>}
                  </span>
                </div>
                {item.currentStep && <div className="flex items-center gap-1" data-id="element-1190">
                    {(() => {
                const Icon = productionSteps[item.currentStep].icon;
                return <Icon className="h-4 w-4" data-id="element-1191" />;
              })()}
                    <span data-id="element-1192">{productionSteps[item.currentStep].label}</span>
                  </div>}
              </div>
              {item.status !== 'planned' && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1193">
                  <ProductionTimeline currentStep={item.currentStep} data-id="element-1194" />
                </div>}
            </div>
          </div>)}
        {editingItem && <QuantityEditModal isOpen={true} onClose={() => setEditingItem(null)} item={allTrackingItems.find(item => item.id === editingItem)} onUpdate={quantities => handleQuantityUpdate(editingItem, quantities)} data-id="element-1195" />}
      </div>
      <BatchDetailModal isOpen={!!selectedItem} onClose={() => setSelectedItem(null)} details={mockTrackingDetails} data-id="element-1196" />
    </div>;
}